import { useExpandableRow } from "./useExpandableRow";
import { Link } from "@mui/material";
import { useCallback } from "react";
/**
 * Usage example:
 * ```
  <ExpandableCell items={items} collapsedLines={5} onClick={() => {loadMoreFunction}} render={(lines) => 
    {lines?.map((line, idx) => (
        <TruncateTypography key={idx} showTooltip variant="body">
          {line}
        </TruncateTypography>
      ))}  
    }>
  </ExpandableCell>
 * ```
 */

interface ExpandableCellProps<T> {
  items: T[];
  rowId: string | number;
  collapsedLines?: number;
  totalCount?: number;
  clickDisabled?: boolean;
  viewMoreDisabled?: boolean;
  onClick?: () => void;
  render: (lines: T[]) => React.ReactNode;
  viewMoreText?: string | ((remainingCount: number) => string);
  viewLessText?: string;
  hideUnderline?: boolean;
}

// Requires use within a omnisearch expandable grid
export function ExpandableCell<T>({
  items,
  rowId,
  collapsedLines = 5,
  totalCount = undefined,
  onClick = () => {},
  clickDisabled = false,
  viewMoreDisabled = false,
  render,
  viewMoreText = "View more",
  viewLessText = "View less",
  hideUnderline = false,
}: ExpandableCellProps<T>) {
  const { isExpanded, toggleRow } = useExpandableRow(rowId);

  const toggleExpand = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      toggleRow();
      onClick();
    },
    [onClick, toggleRow]
  );

  const lines = isExpanded ? items : items?.slice(0, collapsedLines);

  // if totalCount is not provided, use the length of items, to support legacy usage
  const total = totalCount ?? items.length;
  const showLink = !viewMoreDisabled && total > collapsedLines;
  const remainingCount = total - collapsedLines;

  const moreText =
    typeof viewMoreText === "function"
      ? viewMoreText(remainingCount)
      : viewMoreText;

  return (
    <div>
      {render(lines)}
      {showLink && (
        <Link
          type="button"
          component="button"
          sx={{
            fontSize: "inherit",
            ...(hideUnderline && {
              textDecoration: "none",
              "&:hover": { textDecoration: "underline" },
            }),
          }}
          onClick={toggleExpand}
          disabled={clickDisabled}
        >
          {isExpanded ? viewLessText : moreText}
        </Link>
      )}
    </div>
  );
}
